// src/lib/facebookPixel.ts
import { trackLeadX } from "./x-pixel";

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
  }
}

/**
 * Gera eventID único para deduplicação Pixel + CAPI (Sprint 3).
 * D-58 ANALYTICS-06: usa crypto.randomUUID() (UUIDv4 padrão); fallback
 * para timestamp+random só pra browsers muito antigos (não esperado em mobile pt-BR).
 */
export function generateEventId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 11);
  return `lead_${timestamp}_${random}`;
}

/** Wrapper com pequeno retry (Pixel carrega de forma assíncrona) */
function callFbq(...args: unknown[]) {
  if (typeof window === "undefined") return;
  if (typeof window.fbq === "function") {
    window.fbq(...args);
  } else {
    setTimeout(() => {
      if (typeof window.fbq === "function") {
        window.fbq(...args);
      } else if (import.meta.env.DEV) {
        console.warn("[fbq] indisponível. Evento não enviado:", args);
      }
    }, 400);
  }
}

export const fbq = (...args: unknown[]) => callFbq(...args);

/** Eventos padrão */
export function trackPageView() {
  fbq("track", "PageView");
}

/**
 * Params para Lead.
 *
 * SEM value/currency de proposito (removido em 2026-08-22): o site nao vende nada,
 * entrar no grupo e gratuito. Mandavamos value 0 em TODO evento e a Meta acusava
 * "todos os eventos Lead enviam os mesmos dados de preco" e "90% dos dados de preco
 * contem problemas de formatacao ou valores ausentes" — zero e lido como invalido e
 * so sujava o calculo de ROAS. Nao declarar valor e mais honesto do que declarar 0.
 * Se um dia houver valor estimado por lead, e so reintroduzir aqui e no lead.php.
 */
export interface LeadEventParams {
  content_name: string;
  content_category: string;
  channel: string;
  destination: string;
  location: string;
}

/** Dispara evento Lead com eventID para deduplicação */
export function trackLead(params: LeadEventParams, eventId?: string) {
  if (eventId) {
    fbq("track", "Lead", params, { eventID: eventId });
  } else {
    fbq("track", "Lead", params);
  }
}

/** Opcional: Contact para outros fluxos */
export function trackContact(params?: Record<string, unknown>) {
  fbq("track", "Contact", params);
}

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
  return m ? decodeURIComponent(m[1]) : null;
}

function writeCookie(name: string, value: string, days: number): void {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${days * 86400}; Path=/; SameSite=Lax`;
}

/**
 * Garante os identificadores da Meta SEM depender do fbevents.js.
 *
 * PROBLEMA (reportado pela Meta em 2026-08-18, "user_data ausente"): o Pixel só
 * carrega no primeiro toque (otimização de performance), mas o clique no CTA
 * dispara a CAPI na hora — o cookie _fbp ainda não existe, então o evento
 * server-side ia só com IP + user-agent e a Meta não conseguia atribuir.
 *
 * Aqui criamos os cookies no formato oficial da Meta quando ausentes. O
 * fbevents.js REUTILIZA um _fbp existente, então navegador e servidor mandam o
 * MESMO id — o que também melhora a deduplicação.
 *   fbp = fb.1.<timestamp>.<random>
 *   fbc = fb.1.<timestamp>.<fbclid da URL>   ← liga o lead ao anúncio clicado
 */
function ensureMetaIds(): { fbp: string | null; fbc: string | null } {
  let fbp = readCookie("_fbp");
  if (!fbp) {
    fbp = `fb.1.${Date.now()}.${Math.floor(Math.random() * 1e10)}`;
    writeCookie("_fbp", fbp, 90);
  }

  let fbc = readCookie("_fbc");
  if (!fbc) {
    const fbclid = new URLSearchParams(window.location.search).get("fbclid");
    if (fbclid) {
      fbc = `fb.1.${Date.now()}.${fbclid}`;
      writeCookie("_fbc", fbc, 90);
    }
  }
  return { fbp, fbc };
}

/** session_id (anônimo, já usado nos experimentos) → external_id; o PHP faz o hash. */
function readSessionId(): string | null {
  try {
    const raw = localStorage.getItem("session_id");
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as { id?: string };
      return parsed?.id ?? null;
    } catch {
      return raw; // formato antigo (string pura)
    }
  } catch {
    return null; // localStorage bloqueado
  }
}

/**
 * CAPI (server-side) — espelha o Lead do navegador pro endpoint /api/lead,
 * que reenvia pra Graph API da Meta com o MESMO event_id (dedup D-58). O PHP lê
 * fbp/fbc dos cookies (mesma origem) + IP/UA; aqui só mandamos id, url e custom_data.
 * Fire-and-forget: NUNCA bloqueia nem lança (falha/404 em dev é engolido).
 */
function sendCapiLead(eventId: string, params: LeadEventParams): void {
  if (typeof window === "undefined" || typeof fetch !== "function") return;
  try {
    const { fbp, fbc } = ensureMetaIds();
    const body = JSON.stringify({
      event_id: eventId,
      event_source_url: window.location.href,
      // Enviados no corpo TAMBÉM (não só via cookie): o PHP prefere o cookie e
      // cai pra cá, cobrindo o caso do cookie recém-criado nesta mesma chamada.
      fbp,
      fbc,
      external_id: readSessionId(),
      custom_data: {
        content_name: params.content_name,
        content_category: params.content_category,
        channel: params.channel,
        destination: params.destination,
      },
    });
    void fetch("/api/lead", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true, // sobrevive à navegação (window.open logo depois)
      credentials: "same-origin", // envia cookies _fbp/_fbc pro PHP
    }).catch(() => {});
  } catch {
    /* CAPI nunca pode quebrar o fluxo de conversão */
  }
}

/**
 * Dispara Lead com eventID único e aguarda 500ms antes de abrir o link.
 * Retorna Promise para permitir async/await e feedback visual.
 *
 * D-58 ANALYTICS-06: se `extra.eventId` for fornecido, USA-O em vez de gerar
 * um novo internamente. Isso permite que o caller (Index.tsx, StickyBar.tsx)
 * gere o ID UMA VEZ via `generateEventId()`, passe pra `trackCtaClick` (GA4)
 * E pra `trackLeadAndGo` (Pixel) — preservando o invariant Pixel `Lead`
 * `eventID` === GA4 `cta_click` `event_id` para futuro CAPI dedup (Sprint 3).
 */
/**
 * Grupo tematico (ex.: Camping). Mesmo evento Lead, mesmo pixel — so muda o
 * content_name e o destination, para separar no Gerenciador de Eventos (e em
 * conversao personalizada) o lead do grupo principal do lead de um nicho.
 * content_category continua "social_group" de proposito: qualquer filtro ja
 * existente por categoria segue contando os dois.
 */
export interface LeadGroup {
  /** minusculo, sem espaco — vai no destination: "whatsapp_<slug>_group" */
  slug: string;
  /** legivel — vai no content_name: "WhatsApp Group - <label>" */
  label: string;
}

export function trackLeadAndGo(
  url: string,
  destino: "whatsapp" | "telegram",
  extra?: { location?: string; eventId?: string; group?: LeadGroup }
): Promise<void> {
  return new Promise((resolve) => {
    // D-58: usa eventId compartilhado se o caller forneceu; senão, gera novo
    const eventId = extra?.eventId ?? generateEventId();

    const canal = destino === "whatsapp" ? "WhatsApp" : "Telegram";
    const grupo = extra?.group;
    const params: LeadEventParams = {
      content_name: grupo ? `${canal} Group - ${grupo.label}` : `${canal} Group`,
      content_category: "social_group",
      channel: canal,
      destination: grupo ? `${destino}_${grupo.slug}_group` : `${destino}_group`,
      location: extra?.location ?? "Hero",
    };

    trackLead(params, eventId);
    // Espelha server-side (CAPI) com o mesmo event_id → dedup Pixel + CAPI (D-58)
    sendCapiLead(eventId, params);
    // Mesmo Lead no pixel do X Ads (src/lib/x-pixel.ts)
    trackLeadX(eventId);

    // 500ms para garantir envio do evento antes do redirect
    setTimeout(() => {
      window.open(url, "_blank", "noopener,noreferrer");
      resolve();
    }, 500);
  });
}
